import React, { useState } from 'react';
import { Modal, Button, Form, Row, Col, ListGroup, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { Formik, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';
import { FaFilePdf, FaTimes, FaUpload } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { selectStylesSm, DropdownIndicator } from '~/Styles/reactSelectStyles';
import {
  anexarDocumentosContrato,
  uploadTempContrato,
} from '~/Services/Juridico/ControleContratoService';
import { schemaAnexar } from './validationSchemas';
import { extrairMensagemErro } from './useControleContrato';

// Limite padrão 600 KB; supervisor GED (caract 257) pode até 10 MB (Q-10 B)
const LIMITE_PADRAO = 600 * 1024;
const LIMITE_SUPERVISOR = 10 * 1024 * 1024;
const CARACT_SUPERVISOR_GED = 257;

const SITUACOES_REGISTRO = [
  { value: 1, label: 'Matrícula Atualizada' },
  { value: 2, label: 'Rescisão Repasse' },
  { value: 3, label: 'Regulamento' },
  { value: 4, label: 'Outros' },
];

// Prefixo CLDB. mantido em 3 tipos específicos (Q-09 v3 A)
const TITULO_PREFIXO = { 1: 'CLDB.', 2: 'CLDB.', 3: 'CLDB.' };

const ModalAnexarDocumentos = ({ show, onHide, contrato, onSalvo }) => {
  const caracteristicas = useSelector((state) => state.auth.caracteristicas);
  const isSupervisorGed = caracteristicas && caracteristicas.includes(CARACT_SUPERVISOR_GED);
  const limiteArquivo = isSupervisorGed ? LIMITE_SUPERVISOR : LIMITE_PADRAO;

  const [arquivosSelecionados, setArquivosSelecionados] = useState([]);
  const [fazendoUpload, setFazendoUpload] = useState(false);

  if (!contrato) return null;

  const formatarTamanho = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleArquivoChange = (e, setFieldValue) => {
    const novos = Array.from(e.target.files || []);
    const validos = novos.filter((f) => {
      if (f.type !== 'application/pdf') {
        toast.error(`${f.name}: apenas arquivos PDF são aceitos.`);
        return false;
      }
      if (f.size > limiteArquivo) {
        toast.error(
          `${f.name}: arquivo excede o limite de ${formatarTamanho(limiteArquivo)}.`
        );
        return false;
      }
      return true;
    });
    const lista = [...arquivosSelecionados, ...validos];
    setArquivosSelecionados(lista);
    setFieldValue('anexos', lista);
    e.target.value = '';
  };

  const removerArquivo = (idx, setFieldValue) => {
    const lista = arquivosSelecionados.filter((_, i) => i !== idx);
    setArquivosSelecionados(lista);
    setFieldValue('anexos', lista);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setFazendoUpload(true);
    try {
      const storageKeys = [];
      for (const arquivo of arquivosSelecionados) {
        const fd = new FormData();
        fd.append('arquivo', arquivo);
        fd.append('ge', String(contrato.codigo_grupo_empresa));
        const r = await uploadTempContrato(fd);
        storageKeys.push({
          storageKey: r.storage_key,
          nomeOriginal: arquivo.name,
          tamanho: arquivo.size,
          tipo: 'B',
        });
      }

      const situacao = Number(values.situacaoRegistro);
      const prefixo = TITULO_PREFIXO[situacao] || '';
      await anexarDocumentosContrato({
        codigoGrupoEmpresa: contrato.codigo_grupo_empresa,
        codigoConta: contrato.conta,
        codigoBoleto: contrato.boleto,
        situacaoRegistro: situacao,
        anexos: storageKeys.map((a) => ({
          ...a,
          nomeOriginal: `${prefixo}${a.nomeOriginal}`,
        })),
      });

      toast.success('Documentos anexados com sucesso.');
      setArquivosSelecionados([]);
      resetForm();
      onSalvo();
    } catch (err) {
      toast.error(extrairMensagemErro(err));
    } finally {
      setFazendoUpload(false);
      setSubmitting(false);
    }
  };

  const valoresIniciais = { situacaoRegistro: '', anexos: [] };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Anexar Documentos — Boleto {contrato.boleto}</Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={valoresIniciais}
        validationSchema={schemaAnexar}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit: formSubmit, isSubmitting, setFieldValue, values }) => (
          <Form onSubmit={formSubmit}>
            <Modal.Body>
              <Row>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label className="small mb-1">Situação do Registro *</Form.Label>
                    {(() => {
                      const opcoes = SITUACOES_REGISTRO.map((s) => ({
                        value: s.value,
                        label: s.value <= 3 ? `CLDB. ${s.label}` : s.label,
                      }));
                      return (
                        <Select
                          placeholder="Selecione..."
                          options={opcoes}
                          value={
                            opcoes.find(
                              (o) => o.value === Number(values.situacaoRegistro)
                            ) || null
                          }
                          onChange={(sel) =>
                            setFieldValue('situacaoRegistro', sel ? sel.value : '')
                          }
                          isClearable
                          styles={selectStylesSm}
                          components={{ DropdownIndicator }}
                        />
                      );
                    })()}
                    <ErrorMessage name="situacaoRegistro">
                      {(msg) => <small className="text-danger">{msg}</small>}
                    </ErrorMessage>
                  </Form.Group>
                </Col>
                <Col xs={12} sm={6}>
                  <Form.Group>
                    <Form.Label className="small mb-1">
                      Arquivos PDF
                      <span className="text-muted ml-1">
                        (limite: {formatarTamanho(limiteArquivo)})
                      </span>
                    </Form.Label>
                    <div>
                      <label
                        htmlFor="input-upload-contratos"
                        className="btn btn-sm btn-outline-secondary"
                        style={{ cursor: 'pointer' }}
                      >
                        <FaUpload className="mr-1" /> Selecionar PDF
                      </label>
                      <input
                        id="input-upload-contratos"
                        type="file"
                        accept="application/pdf"
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => handleArquivoChange(e, setFieldValue)}
                      />
                    </div>
                    <ErrorMessage name="anexos">
                      {(msg) => <small className="text-danger d-block mt-1">{msg}</small>}
                    </ErrorMessage>
                  </Form.Group>
                </Col>
              </Row>
              {arquivosSelecionados.length > 0 && (
                <ListGroup className="mt-2">
                  {arquivosSelecionados.map((f, idx) => (
                    <ListGroup.Item
                      key={`${f.name}-${idx}`}
                      className="d-flex justify-content-between align-items-center py-1"
                    >
                      <span>
                        <FaFilePdf className="text-danger mr-1" />
                        {f.name}
                        <Badge variant="light" className="ml-2">
                          {formatarTamanho(f.size)}
                        </Badge>
                      </span>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => removerArquivo(idx, setFieldValue)}
                      >
                        <FaTimes />
                      </Button>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}

            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                size="sm"
                onClick={onHide}
                disabled={isSubmitting || fazendoUpload}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={isSubmitting || fazendoUpload || arquivosSelecionados.length === 0}
              >
                {fazendoUpload ? 'Enviando...' : 'Anexar Documentos'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ModalAnexarDocumentos;
